import { useState, useEffect, useMemo } from "react";
import { CalendarDays, MapPin, Clock, Users, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level constants so they don't trigger re-renders when passed as props
const CALENDAR_MODIFIERS_STYLES = {
  event: {
    backgroundColor: "#0f766e",
    color: "white",
    borderRadius: "50%",
  },
};

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [rsvpData, setRsvpData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street_address: "",
    address_line_2: "",
    city: "",
    state: "",
    zip_code: "",
    party_size: 1,
    live_with_scd: "",
    who_has_sickle_cell: "",
    hematologist_name: "",
    taking_medications: "",
    medication_list: "",
    gene_therapy_interest: "",
    genotype: "",
    has_pcp: "",
    age: "",
    gender: "",
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data || []);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleRSVP = async (e) => {
    e.preventDefault();
    
    // Validate required health information fields
    const requiredHealthFields = [
      { field: 'live_with_scd', label: 'Do you live with Sickle Cell Disease?' },
      { field: 'who_has_sickle_cell', label: 'Who Has Sickle Cell?' },
      { field: 'has_pcp', label: 'Do you have a Primary Care Physician?' },
      { field: 'taking_medications', label: 'Are you currently taking medications?' },
      { field: 'gene_therapy_interest', label: 'Interested in Gene Therapy?' },
      { field: 'genotype', label: 'Genotype' },
      { field: 'age', label: 'Age' },
      { field: 'gender', label: 'Gender' },
    ];
    
    for (const { field, label } of requiredHealthFields) {
      if (!rsvpData[field]) {
        toast.error(`Please fill in: ${label}`);
        return;
      }
    }
    
    // If taking medications, medication list is required
    if (rsvpData.taking_medications === "Yes" && !rsvpData.medication_list) {
      toast.error("Please list your medications");
      return;
    }
    
    setRsvpLoading(true);

    try {
      await axios.post(`${API}/events/${selectedEvent.id}/rsvp`, {
        event_id: selectedEvent.id,
        ...rsvpData,
      });

      toast.success("RSVP successful! We look forward to seeing you.");
      setRsvpOpen(false);
      setRsvpData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        street_address: "",
        address_line_2: "",
        city: "",
        state: "",
        zip_code: "",
        party_size: 1,
        live_with_scd: "",
        who_has_sickle_cell: "",
        hematologist_name: "",
        taking_medications: "",
        medication_list: "",
        gene_therapy_interest: "",
        genotype: "",
        has_pcp: "",
        age: "",
        gender: "",
      });
      
      // Update local event count
      setEvents(events.map(ev => 
        ev.id === selectedEvent.id 
          ? { ...ev, rsvp_count: (ev.rsvp_count || 0) + rsvpData.party_size }
          : ev
      ));
    } catch (error) {
      toast.error("Unable to submit RSVP. Please try again.");
    } finally {
      setRsvpLoading(false);
    }
  };

  const openRSVPDialog = (event) => {
    setSelectedEvent(event);
    setRsvpOpen(true);
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const eventDates = useMemo(
    () =>
      events.map((event) => {
        const [year, month, day] = event.date.split("-").map(Number);
        return new Date(year, month - 1, day);
      }),
    [events]
  );

  const calendarModifiers = useMemo(() => ({ event: eventDates }), [eventDates]);

  return (
    <div className="pt-20" data-testid="events-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Join Us
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Upcoming <span className="text-amber-700 italic">Events</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Connect with our community through educational symposiums, awareness events, and support gatherings throughout the year.
            </p>
          </div>
        </div>
      </section>

      {/* Events Grid with Calendar */}
      <section className="py-16 bg-white" data-testid="events-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Calendar Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <h3 className="font-serif text-xl font-semibold text-slate-900 mb-4">
                    Event Calendar
                  </h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    modifiers={calendarModifiers}
                    modifiersStyles={CALENDAR_MODIFIERS_STYLES}
                    className="rounded-md border"
                  />
                  <p className="text-sm text-slate-500 mt-4">
                    <span className="inline-block w-3 h-3 bg-teal-700 rounded-full mr-2"></span>
                    Events scheduled
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Events List */}
            <div className="lg:col-span-2">
              <h2 className="font-serif text-2xl font-semibold text-slate-900 mb-8">
                2026 Events
              </h2>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : events.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No upcoming events at this time.</p>
                  </CardContent>
                </Card>
              ) : (
                events.map((event) => (
                  <Card key={event.id} className="mb-6 card-hover" data-testid={`event-${event.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="md:w-24 text-center md:text-left">
                          <div className="bg-teal-700 text-white rounded-lg p-3 inline-block md:block">
                            <p className="text-2xl font-bold">{event.date.split('-')[2]}</p>
                            <p className="text-sm uppercase">
                              {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">
                            {event.title}
                          </h3>
                          <p className="text-slate-600 mb-4 line-clamp-2">
                            {event.description}
                          </p>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4" />
                              {formatDate(event.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {event.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {event.location}
                            </span>
                          </div>

                          <Button
                            onClick={() => openRSVPDialog(event)}
                            className="bg-teal-700 hover:bg-teal-800 text-white"
                            data-testid={`rsvp-btn-${event.id}`}
                          >
                            RSVP Now
                            <ChevronRight className="ml-1 w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 2027 Teaser Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-teal-900 text-white" data-testid="events-2027-teaser">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-amber-400 font-semibold tracking-widest uppercase text-xs md:text-sm mb-4">
              What&apos;s Next
            </p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6 leading-tight">
              2027 Event Calendar
              <span className="block text-amber-400 mt-1 text-2xl md:text-3xl italic font-normal">Coming Soon</span>
            </h2>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
              We&apos;re curating a full calendar of advocacy days, community walks,
              education symposia, and family gatherings for 2027. Be the first to hear
              when dates drop — subscribe to our newsletter and we&apos;ll send the schedule
              straight to your inbox.
            </p>
            <a
              href="/#newsletter"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-4 rounded-md transition-colors"
              data-testid="teaser-subscribe-link"
            >
              Get Early Access
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
            </a>
          </div>
        </div>
      </section>

      {/* RSVP Dialog */}
      <Dialog open={rsvpOpen} onOpenChange={setRsvpOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">RSVP for Event</DialogTitle>
            <DialogDescription>
              {selectedEvent?.title} - {selectedEvent?.date && formatDate(selectedEvent.date)}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRSVP} className="space-y-6 mt-4">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    type="text"
                    value={rsvpData.first_name}
                    onChange={(e) => setRsvpData({ ...rsvpData, first_name: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="rsvp-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    type="text"
                    value={rsvpData.last_name}
                    onChange={(e) => setRsvpData({ ...rsvpData, last_name: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="rsvp-last-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={rsvpData.email}
                    onChange={(e) => setRsvpData({ ...rsvpData, email: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="rsvp-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={rsvpData.phone}
                    onChange={(e) => setRsvpData({ ...rsvpData, phone: e.target.value })}
                    className="mt-1"
                    data-testid="rsvp-phone"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Address</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    type="text"
                    value={rsvpData.street_address}
                    onChange={(e) => setRsvpData({ ...rsvpData, street_address: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="address_line_2">Address Line 2</Label>
                  <Input
                    id="address_line_2"
                    type="text"
                    value={rsvpData.address_line_2}
                    onChange={(e) => setRsvpData({ ...rsvpData, address_line_2: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      type="text"
                      value={rsvpData.city}
                      onChange={(e) => setRsvpData({ ...rsvpData, city: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Select value={rsvpData.state} onValueChange={(value) => setRsvpData({ ...rsvpData, state: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="zip_code">Zip Code</Label>
                    <Input
                      id="zip_code"
                      type="text"
                      value={rsvpData.zip_code}
                      onChange={(e) => setRsvpData({ ...rsvpData, zip_code: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Event Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="party_size">Party Size *</Label>
                  <Input
                    id="party_size"
                    type="number"
                    min="1"
                    max="20"
                    value={rsvpData.party_size}
                    onChange={(e) => setRsvpData({ ...rsvpData, party_size: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                    data-testid="rsvp-party-size"
                  />
                </div>
              </div>
            </div>

            {/* Health Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Health Information <span className="text-red-500">*</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="live_with_scd">Do you live with Sickle Cell Disease? <span className="text-red-500">*</span></Label>
                  <Select value={rsvpData.live_with_scd} onValueChange={(value) => setRsvpData({ ...rsvpData, live_with_scd: value })} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="who_has_sickle_cell">Who Has Sickle Cell? <span className="text-red-500">*</span></Label>
                  <Input
                    id="who_has_sickle_cell"
                    type="text"
                    placeholder="Self, Child, Family Member..."
                    value={rsvpData.who_has_sickle_cell}
                    onChange={(e) => setRsvpData({ ...rsvpData, who_has_sickle_cell: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="hematologist_name">Hematologist Name</Label>
                  <Input
                    id="hematologist_name"
                    type="text"
                    value={rsvpData.hematologist_name}
                    onChange={(e) => setRsvpData({ ...rsvpData, hematologist_name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="has_pcp">Do you have a Primary Care Physician? <span className="text-red-500">*</span></Label>
                  <Select value={rsvpData.has_pcp} onValueChange={(value) => setRsvpData({ ...rsvpData, has_pcp: value })} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="taking_medications">Are you currently taking medications? <span className="text-red-500">*</span></Label>
                  <Select value={rsvpData.taking_medications} onValueChange={(value) => setRsvpData({ ...rsvpData, taking_medications: value })} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="gene_therapy_interest">Interested in Gene Therapy? <span className="text-red-500">*</span></Label>
                  <Select value={rsvpData.gene_therapy_interest} onValueChange={(value) => setRsvpData({ ...rsvpData, gene_therapy_interest: value })} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Maybe">Maybe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {rsvpData.taking_medications === "Yes" && (
                <div>
                  <Label htmlFor="medication_list">Please list your medications <span className="text-red-500">*</span></Label>
                  <Textarea
                    id="medication_list"
                    value={rsvpData.medication_list}
                    onChange={(e) => setRsvpData({ ...rsvpData, medication_list: e.target.value })}
                    required
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="genotype">Genotype <span className="text-red-500">*</span></Label>
                  <Select value={rsvpData.genotype} onValueChange={(value) => setRsvpData({ ...rsvpData, genotype: value })} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HbSS">HbSS</SelectItem>
                      <SelectItem value="HbSC">HbSC</SelectItem>
                      <SelectItem value="HbS Beta Thalassemia">HbS Beta Thalassemia</SelectItem>
                      <SelectItem value="HbSD">HbSD</SelectItem>
                      <SelectItem value="HbSE">HbSE</SelectItem>
                      <SelectItem value="Trait">Sickle Cell Trait</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="age">Age <span className="text-red-500">*</span></Label>
                  <Input
                    id="age"
                    type="text"
                    value={rsvpData.age}
                    onChange={(e) => setRsvpData({ ...rsvpData, age: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender <span className="text-red-500">*</span></Label>
                  <Select value={rsvpData.gender} onValueChange={(value) => setRsvpData({ ...rsvpData, gender: value })} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Non-binary">Non-binary</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRsvpOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={rsvpLoading}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="rsvp-submit-btn"
              >
                {rsvpLoading ? "Submitting..." : "Confirm RSVP"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-16 bg-teal-700">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Want to Host an Event?
          </h2>
          <p className="text-teal-100 mb-6">
            Partner with us to organize awareness events, fundraisers, or community programs.
          </p>
          <Button className="bg-white text-teal-700 hover:bg-teal-50 font-bold uppercase tracking-wide text-sm px-8">
            Contact Us
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
